import {Filter} from '@github-ui/filter'
import type {FilterQuery} from '@github-ui/filter-core'
import {BookmarkIcon, DuplicateIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu} from '@primer/react'
import {clsx} from 'clsx'
import {type ReactNode, useCallback, useRef, useState} from 'react'

import {BUTTON_LABELS, LABELS} from '../../constants'
import {useDuplicateSavedViewDialog} from '../../hooks/use-duplicate-saved-view-dialog'
import {useSavedViewFilterProviders} from '../../hooks/use-saved-view-filter-providers'
import {useSavedViewFilterSettings} from '../../hooks/use-saved-view-filter-settings'
import type {SavedView} from '../../types'
import styles from './SavedViewFilterBar.module.css'

interface SavedViewFilterBarProps<TConfiguration> {
  owner: string
  repo: string
  /** The view as currently saved on the server — used to detect dirty state and to preserve fields on save. */
  savedView: SavedView
  /** The currently active query (URL or stored). */
  currentQuery: string
  /** Push a new query to the URL (and the live preview). */
  onQueryChange: (query: string) => void
  /** Persist the applied query and any host-managed configuration. Errors are surfaced at page level. */
  onSaveChanges: () => void
  /** True while a save-changes request is in flight. */
  isSaving: boolean
  isConfigurationDirty?: boolean
  /** Applied host-owned configuration to persist when saving changes to a new view. */
  configuration?: TConfiguration
  isNavigating?: boolean
  /** Whether the viewer can save changes to or duplicate the view. */
  canManage: boolean
  /**
   * Optional controls rendered before the save action. When provided, the filter bar
   * groups the leading actions and save together and lets the row wrap on narrow widths.
   */
  leadingActions?: ReactNode
}

export function SavedViewFilterBar<TConfiguration = never>({
  owner,
  repo,
  savedView,
  currentQuery,
  onQueryChange,
  onSaveChanges,
  isSaving,
  isConfigurationDirty = false,
  configuration,
  isNavigating = false,
  canManage,
  leadingActions,
}: SavedViewFilterBarProps<TConfiguration>) {
  const [filterValue, setFilterValue] = useState(currentQuery)
  const [syncedQuery, setSyncedQuery] = useState(currentQuery)
  if (syncedQuery !== currentQuery) {
    setSyncedQuery(currentQuery)
    setFilterValue(currentQuery)
  }

  const filterProviders = useSavedViewFilterProviders({owner, repo})
  const filterSettings = useSavedViewFilterSettings()

  const handleSubmit = useCallback((request: FilterQuery) => onQueryChange(request.raw), [onQueryChange])

  const saveMenuButtonRef = useRef<HTMLButtonElement>(null)
  const {openDuplicateDialog, duplicateDialog} = useDuplicateSavedViewDialog<TConfiguration>({
    owner,
    repo,
    returnFocusRef: saveMenuButtonRef,
  })

  const trimmedQuery = currentQuery.trim()
  const isDirty = trimmedQuery !== savedView.query.trim() || isConfigurationDirty

  const handleSaveAsNewView = useCallback(() => {
    openDuplicateDialog({...savedView, query: trimmedQuery}, configuration)
  }, [configuration, openDuplicateDialog, savedView, trimmedQuery])

  const saveMenu = canManage ? (
    <ActionMenu anchorRef={saveMenuButtonRef}>
      <ActionMenu.Button variant={isDirty ? 'primary' : 'default'} disabled={!isDirty || isSaving || isNavigating}>
        {isSaving ? LABELS.views.saving : BUTTON_LABELS.save}
      </ActionMenu.Button>
      <ActionMenu.Overlay>
        <ActionList>
          <ActionList.Item onSelect={onSaveChanges}>
            <ActionList.LeadingVisual>
              <BookmarkIcon />
            </ActionList.LeadingVisual>
            {LABELS.views.saveChangesToView}
          </ActionList.Item>
          <ActionList.Item onSelect={handleSaveAsNewView}>
            <ActionList.LeadingVisual>
              <DuplicateIcon />
            </ActionList.LeadingVisual>
            {LABELS.views.saveChangesToNewView}
          </ActionList.Item>
        </ActionList>
      </ActionMenu.Overlay>
    </ActionMenu>
  ) : null

  const hasLeadingActions = leadingActions != null

  return (
    <div className={clsx(styles.filterRow, hasLeadingActions && styles.withLeadingActions)}>
      <div className={styles.filterField}>
        <Filter
          id="saved-view-filter"
          label={LABELS.issueSearchInputAriaLabel}
          placeholder={LABELS.issueSearchInputPlaceholder}
          variant="input"
          providers={filterProviders}
          filterValue={filterValue}
          onChange={setFilterValue}
          onSubmit={handleSubmit}
          settings={filterSettings}
          showValidationMessage={false}
          visuallyHideLabel
        />
      </div>
      {hasLeadingActions ? (
        <div className={styles.filterActions}>
          {leadingActions}
          {saveMenu}
        </div>
      ) : (
        saveMenu
      )}
      {duplicateDialog}
    </div>
  )
}
